/**
 * Centralized UI strings (uk-UA only).
 * If multi-locale ever needed, migrate to i18next.
 */

/**
 * Ukrainian plural rules: 1 → singular, 2-4 → few, 0/5+ → many.
 * Source: CLDR plural rules for 'uk'.
 */
function pluralizeParts(n: number): string {
  const mod10 = n % 10
  const mod100 = n % 100
  if (mod10 === 1 && mod100 !== 11) return 'деталь'
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'деталі'
  return 'деталей'
}

/** «1 файл · 3 файли · 104 аркуші» — CLDR 'uk' one / few / many. */
export function pluralUk(n: number, one: string, few: string, many: string): string {
  const mod10 = n % 10
  const mod100 = n % 100
  if (mod10 === 1 && mod100 !== 11) return one
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few
  return many
}

function pluralizeAssemblies(n: number): string {
  const mod10 = n % 10
  const mod100 = n % 100
  if (mod10 === 1 && mod100 !== 11) return 'вузол'
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'вузли'
  return 'вузлів'
}

export const messages = {
  common: {
    save: 'Зберегти',
    cancel: 'Скасувати',
    confirm: 'Підтвердити',
    delete: 'Видалити',
    edit: 'Редагувати',
    create: 'Створити',
    close: 'Закрити',
    loading: 'Завантаження…',
    error: 'Помилка',
    retry: 'Спробувати знову',
    search: 'Пошук',
    filter: 'Фільтр',
    noData: 'Немає даних',
    actions: 'Дії',
    required: 'Обовʼязкове поле',
    /** Shared switch row labels for the unified is_active toggle (IsActiveSwitchField). */
    statusFieldLabel: 'Статус',
    statusActive: 'Активний',
    statusArchived: 'Архів',
    statusActiveHint: 'Видно у списку, доступний для нових документів',
    statusArchivedHint: 'Сховано у фільтрах за замовчуванням',
  },
  nav: {
    projects: 'Проєкти',
    dashboard: 'Дашборд',
    workOrders: 'Наряди на роботу',
    bom: 'Структура',
    nomenclature: 'Номенклатура',
    /** Cross-project Nomenclature page (1С-style). Sits under the global sidebar group. */
    globalNomenclature: 'Номенклатура',
    inventory: 'Склад',
    receipts: 'Приходи',
    deficits: 'Дефіцити',
    admin: 'Адміністрування',
    // Sidebar folder headers (Shell.jsx NavGroup pattern, 1С taxonomy).
    folderDocuments: 'Документи',
    folderInventory: 'Склад',
    folderAnalytics: 'Аналітика',
    folderReferences: 'Довідники',
    // Sidebar-only relabels that differ from page titles.
    productStructure: 'Структура виробу',
    drawings: 'Креслення',
    stock: 'Склад',
    storekeeping: 'Комірник',
    goodsReceipts: 'Прихідний ордер',
    stockReceipts: 'Прихідна накладна',
    productionReports: 'Звіт виробництва',
    materialIssues: 'Розхідний ордер',
    transfers: 'Переміщення',
    reports: 'Звіти',
    /** Hover hints: what each document is for. */
    hints: {
      workOrders: 'Наряди на різку і зварку',
      materialIssues: 'Видача зі складу в цех',
      productionReports: 'Що цех зробив за зміну',
      goodsReceipts: 'Прийом із цеху на склад: готове, обрізки, повернення',
      stockReceipts: 'Прихід від постачальника',
      transfers: 'Переміщення між складами',
      stock: 'Що зараз лежить на складах',
      storekeeping: 'Наряди, яким ще треба видати матеріал чи деталі',
      references: 'Люди, склади, посади, операції, одиниці виміру',
    },
  },
  crumbs: {
    projects: 'Проєкти',
    projectImport: 'Імпорт проєкту',
    drawings: 'Креслення',
    dashboard: 'Дашборд',
    bom: 'Структура виробу',
    deficits: 'Дефіцити',
    inventory: 'Склад',
    receipts: 'Приходи',
    workOrders: 'Наряди на роботу',
    receiptDetail: 'Прихідна накладна №{id}',
    references: 'Довідники',
    refUnits: 'Одиниці виміру',
    refOperations: 'Операції',
    refPositions: 'Посади',
    refUsers: 'Люди',
    refWarehouses: 'Склади',
    refWarehouseNew: 'Новий склад',
    refWarehouseEdit: 'Редагувати склад',
    goodsReceipts: 'Прихідний ордер на товар',
    stockReceipts: 'Прихідна накладна',
    productionReports: 'Звіт виробництва за зміну',
    materialIssues: 'Розхідний ордер на товар',
    transfers: 'Переміщення',
    transferNew: 'Нове переміщення',
  },
  projects: {
    empty: 'Проєктів немає',
    subtitle: 'Список усіх проєктів',
    fallback: 'Проєкт',
    counter: {
      total: (n: number) => `${n} ${pluralUk(n, 'проєкт', 'проєкти', 'проєктів')}`,
      active: (n: number) => `${n} ${pluralUk(n, 'активний', 'активні', 'активних')}`,
    },
    switcher: {
      placeholder: 'Пошук проєкту…',
      selectPrompt: 'Виберіть проєкт',
      notFound: 'Нічого не знайдено',
    },
    columns: {
      code: 'Код',
      name: 'Назва',
      customer: 'Замовник',
      notes: 'Примітка',
      status: 'Статус',
      created: 'Створено',
    },
    status: {
      active: 'Активний',
      archived: 'Архів',
    },
    searchPlaceholder: 'Пошук за назвою / ID / замовником…',
    emptySearch: 'Проєктів не знайдено',
    dashboard: {
      subtitle: '{code} · {name}',
      materialsBom: 'Матеріалів у специфікації',
      assemblies: 'Вузлів у структурі',
      deficitsHint: 'позицій із дефіцитом',
      deficitsFree: 'дефіцитів немає',
      workOrdersSplit: '{draft} у чернетці · {posted} проведено',
      workOrdersNone: 'ще не створено',
      next: {
        title: 'Що далі',
        nothing: 'Нічого не чекає: усі документи проєкту завершено.',
      },
      stages: {
        title: 'Готовність проєкту',
        parts: 'Деталі',
        assemblies: 'Вузли',
        made: 'виготовлено',
        assembled: 'зібрано',
        of: 'з',
        pcs: 'шт',
        kg: 'кг',
        partsOnStock: '{n} шт на складі, ще не у вузлах',
        partsShort: 'бракує {n} шт · {kg} кг',
        asmOnStock: '{n} шт на складі',
        asmBuildable: 'можна зібрати ще {n} з деталей на складі',
      },
      noBom: 'Структуру виробу ще не імпортовано — готовність рахувати нема з чого.',
    },
    buttons: {
      new: 'Новий проєкт',
      import: 'Імпорт з JSON',
      edit: 'Редагувати',
      delete: 'Видалити',
      actions: 'Дії',
    },
    forms: {
      createTitle: 'Створити проєкт',
      createDescription: 'Заповніть основні реквізити проєкту.',
      editTitle: 'Редагувати проєкт',
      editDescription: 'Оновити реквізити проєкту.',
      deleteTitle: 'Видалити проєкт?',
      deleteDescription:
        'Дію не можна скасувати. Видалення неможливе якщо проєкт має специфікацію, наряди на роботу або складські записи.',
      fields: {
        code: 'Код',
        name: 'Назва',
        customer: 'Замовник',
        notes: 'Примітка',
        isActive: 'Активний',
        status: 'Статус',
        statusActiveHint: 'Видно у списку, доступний для нових документів',
        statusArchivedHint: 'Сховано у фільтрах за замовчуванням',
      },
      validation: {
        codeRequired: 'Код обовʼязковий',
        nameRequired: 'Назва обовʼязкова',
        codeDuplicate: 'Проєкт із таким кодом уже існує',
      },
      toasts: {
        created: 'Проєкт створено',
        updated: 'Проєкт оновлено',
        deleted: 'Проєкт видалено',
        saveError: 'Не вдалося зберегти',
        deleteError: 'Не вдалося видалити',
      },
    },
  },
  auth: {
    login: 'Увійти',
    logout: 'Вийти',
    email: 'Email',
    password: 'Пароль',
    loginError: 'Невірний email або пароль',
  },
  sidebar: {
    themeLight: 'Світла тема',
    themeDark: 'Темна тема',
    roleAdmin: 'Адміністратор',
    roleUser: 'Оператор',
  },
  status: {
    draft: 'Чернетка',
    posted: 'Проведено',
  },
  woType: {
    cutter: 'Різальний',
    welder: 'Зварювальний',
  },
  bom: {
    subtitle: 'Розгорніть вузли, щоб переглянути компоненти',
    columns: {
      structure: 'Структура',
      quantity: 'Кількість',
      quantityTotal: 'Всього план.',
      weight: 'Вага, кг',
    },
    summaryStats: (assemblies: number, parts: number, totalWeightKg: string) =>
      `${assemblies} ${pluralizeAssemblies(assemblies)} · ${parts} ${pluralizeParts(parts)} · загальна вага ${totalWeightKg} кг`,
    summaryChips: {
      assemblies: (n: number) => `${n} ${pluralizeAssemblies(n)}`,
      parts: (n: number) => `${n} ${pluralizeParts(n)}`,
      materials: (n: number) => `${n} ${pluralUk(n, 'матеріал', 'матеріали', 'матеріалів')}`,
      totalWeight: (formatted: string) => `вага виробу: ${formatted}`,
    },
    summaryFooter: {
      totalPositions: 'Усього позицій:',
      totalWeight: 'Загальна вага:',
    },
    empty: 'Структура виробу порожня',
    cyclicRef: 'циклічне посилання',
    orphanGroup: 'Деталі без вузла',
    treeControls: {
      expandAll: 'Розгорнути все',
      collapseAll: 'Згорнути все',
      searchPlaceholder: 'Пошук за кодом або назвою…',
      noMatches: 'Нічого не знайдено за запитом',
    },
    addMenu: {
      trigger: 'Додати',
      items: {
        assembly: { label: 'Вузол', description: 'Складальна одиниця' },
        part: { label: 'Деталь', description: 'Виготовлювана деталь' },
        material: { label: 'Матеріал', description: 'Глобальний каталог' },
      },
    },
    addComponent: {
      buttonLabel: 'Додати компонент',
      modalTitle: 'Додати компонент у вузол',
      pickerLabel: 'Деталь або вузол',
      quantityLabel: 'Кількість',
      submit: 'Додати',
      cancel: 'Скасувати',
      saved: 'Компонент додано',
      cycleErrorTitle: 'Створює цикл',
    },
    editComponent: {
      saved: 'Збережено',
    },
    treeDnd: {
      handleTooltip: 'Перетягніть для зміни батьківської збірки',
    },
    inspector: {
      titles: {
        viewMaterial: 'Матеріал',
        viewPart: 'Деталь',
        viewAssembly: 'Вузол',
        editMaterial: 'Редагування матеріалу',
        editPart: 'Редагування деталі',
        editAssembly: 'Редагування вузла',
        createMaterial: 'Новий матеріал',
        createPart: 'Нова деталь',
        createAssembly: 'Новий вузол',
      },
      subtitles: {
        view: 'Перегляд',
        edit: 'Редагування',
        create: 'Заповніть форму',
      },
      actions: {
        edit: 'Редагувати',
        delete: 'Видалити',
      },
      delete: {
        confirmTitle: 'Видалити?',
        confirmDescription:
          'Дію не можна скасувати. Якщо запис використовується в інших місцях — видалення буде заблоковано.',
        confirmLabel: 'Видалити',
        toastDeleted: 'Видалено',
        toastError: 'Помилка видалення',
      },
      emptyState: {
        title: 'Виберіть елемент дерева',
        body: 'Натисніть на вузол, деталь або матеріал ліворуч, щоб переглянути та редагувати дані тут.',
      },
      sections: {
        identity: 'Ідентифікація',
        properties: 'Властивості',
        material: 'Матеріал',
        quantity: 'Кількість',
        audit: 'Аудит',
        notes: 'Нотатки',
        drawing: 'Креслення',
      },
      footer: {
        created: 'Створено',
        updated: 'Оновлено',
      },
      closePanel: 'Закрити панель',
      composition: {
        title: 'Склад',
        direct: 'Прямих компонентів',
        directSplit: (total: number, subAsm: number, parts: number) =>
          `${total} (вузли: ${subAsm} · деталі: ${parts})`,
        totalLeafParts: 'Різних деталей (рекурс.)',
        uniqueMaterials: 'Унікальних матеріалів',
      },
      usedIn: {
        title: 'Використовується у',
        empty: 'Не використовується у жодному вузлі',
        partInAssembly: (qty: number) => `× ${qty}`,
        materialInPart: (count: number) => `у ${count} деталях`,
      },
      drawing: {
        empty: 'Креслення не прикріплено',
        openFullscreen: 'Відкрити повноекранно',
        openPdf: 'Відкрити повний PDF',
        previewAlt: 'Прев’ю креслення {sheet}',
        loadFailed: 'Не вдалося завантажити прев’ю',
        pageOf: 'ст. {page} з {total}',
        viewer: {
          title: '{file} · аркуш {sheet}',
          page: 'ст. {page}',
          zoomIn: 'Збільшити',
          zoomOut: 'Зменшити',
          fit: 'Вписати',
          actualSize: '100%',
          download: 'Завантажити PDF',
          close: 'Закрити',
          prevSheet: 'Попередній аркуш',
          nextSheet: 'Наступний аркуш',
          loading: 'Завантаження PDF…',
          error: 'Не вдалося відкрити PDF',
          retry: 'Спробувати ще раз',
        },
      },
    },
    forms: {
      common: {
        required: 'Обовʼязкове поле',
        nonNegative: 'Має бути ≥ 0',
        positive: 'Має бути > 0',
        saved: 'Збережено',
        created: 'Створено',
        savePending: 'Збереження…',
        save: 'Зберегти',
        saveError: 'Помилка збереження',
        codeAuto: 'Авто-генерується якщо порожньо',
        codeRenameImpact:
          'Зміна коду оновить усі звіти й активні наряди на роботу. PDF-карти, що вже на руках у цеху, залишаться зі старим кодом — це фізичний документ.',
        parentAssemblyRequired: 'Виберіть батьківський вузол',
        materialGlobalHint: 'Глобальний каталог — впливає на всі проекти',
      },
      fields: {
        code: 'Код',
        name: 'Назва',
        unit: 'Одиниця',
        notes: 'Нотатки',
        active: 'Активний',
        material: 'Матеріал',
        section: 'Переріз',
        qtyTotal: 'Кількість на проєкт, шт',
        lengthMm: 'Довжина, мм',
        widthMm: 'Ширина, мм',
        widthFromSection: 'Порожньо — з перерізу (BL6x41 → 41)',
        isSheet: 'Лист',
        isSheetHint: 'Ріжеться в площині: розкрій рахує довжину й ширину листа',
        sheetWidthMm: 'Ширина листа, мм',
        sheetLengthMm: 'Довжина листа, мм',
        unitWeightKg: 'Вага одиниці, кг',
        drawingSheet: 'Креслення',
        quantityPlanned: 'Кількість, план.',
        parentAssembly: 'Батьківський вузол',
      },
    },
  },
  deficits: {
    inWork: (n: number) => `+${n} у цеху`,
    columns: {
      structure: 'Структура',
      required: 'Потрібно',
      made: 'Зроблено',
      deficit: 'Не вистачає',
      onStock: 'Вже є',
      stillNeeded: 'Ще треба',
      buildable: 'Можна зібрати',
    },
    badges: {
      covered: 'Забезпечено',
      cyclicRef: 'циклічне посилання',
    },
    views: {
      part: 'Деталь',
      material: 'Матеріал',
      assembly: 'Вузол',
      byAssemblies: 'Розбивка по вузлах',
      noAssemblies: 'Деталь не входить у жоден вузол',
      noComponents: 'Вузол без складу (немає BOM)',
      totalForMaterial: 'Разом по матеріалу',
      weight: 'Вага',
      weightDeficit: '~{t} т бракує',
      weightKg: '{kg} кг',
    },
    empty: 'Дефіцитів не виявлено — всі компоненти забезпечені',
    emptyFiltered: 'За обраними фільтрами нічого не знайдено',
    noBom: {
      title: 'У проєкті немає складу виробу (BOM)',
      body: 'Звіт рахує потребу лише від вузлів проєкту. Імпортуйте склад виробу — і тут з’являться план, готовність і дефіцит.',
      cta: 'Імпортувати BOM',
    },
    actions: {
      expandAll: 'Розгорнути все',
      collapseAll: 'Згорнути все',
      recompute: 'Перерахувати',
    },
    summary: {
      readiness: 'Готовність',
      readinessHint: 'Покрито з потреби проєкту',
      readinessStagesHint: 'Середнє стадій: деталі та вузли',
      partsReadiness: 'Готовність деталей',
      onStock: '{n} на складі',
      partsCovered: 'Деталей покрито',
      partsMade: 'Деталей виготовлено',
      assembliesMade: 'Вузлів зібрано',
      assembliesOnStock: 'Вузлів на складі',
      buildable: '+{n} можна зібрати',
      ofTotal: 'з {total}',
      weightHint: '~{t} т бракує',
      surplus: '+{n} понад план',
      deficitLine: '{n} дефіцит',
      deficitLineWeight: '{n} дефіцит · ~{t} т',
    },
    filters: {
      label: 'Показати',
      all: 'Усе',
      withDeficit: 'З дефіцитом',
      material: 'Матеріал',
      materialPlaceholder: 'Усі матеріали',
      part: 'Деталь',
      partPlaceholder: 'Усі деталі',
      assembly: 'Вузол',
      assemblyPlaceholder: 'Усі вузли',
      search: 'Пошук',
      searchPlaceholder: 'За кодом або назвою…',
      reset: 'Скинути',
      view: 'Вид',
      viewStructure: 'По структурі',
      viewAssembly: 'По вузлах',
      viewMaterial: 'По матеріалу',
      viewPart: 'По деталі',
    },
  },
  receipts: {
    subtitle: 'Усі приходи матеріалів за проєктом',
    columns: {
      id: '№',
      title: 'Назва',
      documentNumber: 'Документ',
      positions: 'Позицій',
      status: 'Статус',
      createdAt: 'Створено',
      postedAt: 'Проведено',
      totalWeightKg: 'Вага, кг',
    },
    empty: 'Приходів немає — створіть перший',
    create: 'Прихід',
    createDialog: {
      title: 'Створити прихід',
      description: 'Чернетку можна редагувати — рядки додаються на сторінці деталей.',
      fields: {
        title: 'Назва',
        documentNumber: 'Номер документа',
        notes: 'Нотатки',
      },
      submit: 'Створити',
      pending: 'Створення…',
      titleRequired: 'Назва обовʼязкова',
      success: 'Прихід створено',
      error: 'Не вдалося створити прихід',
    },
    detail: {
      sectionLabel: 'Шапка документа',
      fallbackTitle: 'Прихідна накладна №{id}',
      fields: {
        title: 'Назва',
        documentNumber: 'Документ',
        notes: 'Нотатки',
        createdAt: 'Створено',
        postedAt: 'Проведено',
      },
      actions: {
        post: 'Провести',
        unpost: 'Розпровести',
      },
    },
    lines: {
      title: 'Рядки приходу',
      add: 'Додати рядок',
      delete: 'Видалити',
      empty: 'Прихід поки порожній — додайте перший рядок',
      partLabel: 'Деталь',
      assemblyLabel: 'Складання',
      columns: {
        number: '№',
        material: 'Матеріал',
        quantity: 'Кількість',
        lengthMm: 'Довжина, мм',
        widthMm: 'Ширина, мм',
        weightKg: 'Вага, кг',
        notes: 'Нотатки',
        actions: 'Дії',
      },
      addForm: {
        description: 'Заповніть поля для нового рядка приходу.',
        material: 'Матеріал',
        materialPlaceholder: 'Виберіть матеріал',
        quantity: 'Кількість',
        lengthMm: 'Довжина, мм',
        widthMm: 'Ширина листа, мм',
        weightKg: 'Вага, кг',
        notes: 'Нотатки',
        submit: 'Додати',
        cancel: 'Скасувати',
      },
      confirmDelete: {
        title: 'Видалити рядок?',
        message: 'Рядок №{idx} буде видалено з цього приходу.',
        confirm: 'Видалити',
        cancel: 'Скасувати',
      },
      errors: {
        pickMaterial: 'Виберіть матеріал',
        qtyInvalid: 'Кількість має бути додатнім числом',
        lengthInvalid: 'Довжина має бути додатнім числом або порожньою',
        weightInvalid: 'Вага має бути додатнім числом або порожньою',
      },
    },
    confirmPost: {
      title: 'Провести прихід?',
      description:
        'Буде створено лоти та позиції матеріалів відповідно до рядків. Дія оборотна через розпровести.',
      confirm: 'Провести',
      backdateToggle: 'Заднім числом',
      backdateLabel: 'Дата і час проведення',
      backdateHint:
        'Дата проведення для документа. Час фактичного запису в систему фіксується окремо.',
    },
    confirmUnpost: {
      title: 'Розпровести прихід?',
      description:
        'Створені лоти будуть м’яко видалені, документ повернеться в чернетку. Якщо лоти вже зачеплені іншими документами (споживанням, мерджем) — спочатку розпровести ті.',
      confirm: 'Розпровести',
    },
    toasts: {
      saved: 'Збережено',
      posted: 'Прихід проведено',
      unposted: 'Прихід розпроведено',
      lineAdded: 'Рядок додано',
      lineUpdated: 'Рядок оновлено',
      lineDeleted: 'Рядок видалено',
    },
  },
  workOrders: {
    subtitle: 'Список нарядів на роботи проєкту',
    counterUnit: 'нарядів',
    create: 'Створити',
    taskLineStatus: {
      planned: 'Заплановано',
      in_progress: 'В роботі',
      done: 'Виконано',
    } satisfies Record<'planned' | 'in_progress' | 'done', string>,
    filters: {
      type: 'Тип',
      status: 'Статус',
      all: 'Усі',
      reset: 'Скинути',
    },
    columns: {
      number: '№',
      type: 'Тип',
      workDate: 'Дата робіт',
      team: 'Бригада',
      status: 'Статус',
      postedAt: 'Проведено',
    },
    empty: 'Нарядів ще немає',
    sections: {
      header: 'Реквізити наряду',
      cutPlan: 'План розкрою',
      issueLines: 'Видача матеріалів',
      issueLinesWelder: 'Видача деталей',
      outputLines: 'Випуск',
      taskLines: 'Завдання',
      materialPlan: 'План матеріалів',
      materialIssues: 'Розхідний ордер',
      productionReports: 'Звіт виробництва за зміну',
      goodsReceipts: 'Прихідний ордер',
      progress: 'Прогрес',
    },
    quickCreate: {
      minLabel: 'Створити розхідний',
      minCreated: 'Розхідний ордер створено',
      minError: 'Не вдалося створити розхідний ордер',
      prLabel: 'Створити звіт',
      prCreated: 'Звіт виробництва створено',
      prError: 'Не вдалося створити звіт',
      grCreated: 'Прихідний ордер створено',
      grError: 'Не вдалося створити прихідний ордер',
    },
    materialPlan: {
      columns: {
        material: 'Матеріал',
        lot: 'Лот',
        inWip: 'У цеху',
      },
      empty: 'Немає видач/споживання',
    },
    materialIssuesList: {
      columns: {
        number: '№',
        date: 'Дата',
        recipient: 'Отримувач',
        status: 'Статус',
        actions: 'Дії',
      },
      open: 'Відкрити',
      empty: 'Видач немає',
    },
    goodsReceiptsList: {
      columns: {
        number: '№',
        date: 'Дата',
        storekeeper: 'Прийняв',
        status: 'Статус',
        actions: 'Дії',
      },
      open: 'Відкрити',
      empty: 'Прийомів немає',
    },
    productionReport: {
      create: 'Створити звіт за сьогодні',
      columns: {
        number: 'Звіт',
        shiftDate: 'Дата зміни',
        author: 'Виконавець',
        status: 'Статус',
        actions: 'Дії',
      },
      open: 'Відкрити',
      empty: 'Звітів немає',
      dialog: {
        title: 'Виробничий звіт',
        shiftDate: 'Дата зміни',
        line: {
          taskLine: 'Рядок плану (ID)',
          producedQty: 'Випущено',
          hoursSpent: 'Годин',
        },
        addLine: '+ Рядок',
        submit: 'Зберегти + Провести',
        cancel: 'Скасувати',
        emptyLines: 'Додайте хоча б один рядок',
      },
    },
    actions: {
      post: 'Провести',
      unpost: 'Розпровести',
      delete: 'Видалити',
      optimize: 'Оптимізувати',
    },
    fields: {
      workDate: 'Дата робіт',
      team: 'Бригада',
      notes: 'Нотатки',
      tariff: 'Розцінка, ₴/год',
      shift: 'Зміна',
      company: 'Підприємство',
      period: 'Період',
      position: 'Посада',
      department: 'Підрозділ',
    },
    detail: {
      number: 'Номер',
      type: 'Тип',
      status: 'Статус',
      createdAt: 'Створено',
      postedAt: 'Проведено',
      unknownType: 'Невідомий тип наряду',
    },
    editActual: {
      title: 'Зафіксувати фактичну кількість',
      qty: 'Фактична кількість',
      length: 'Фактична довжина',
      hours: 'Фактичні години',
      reason: 'Причина зміни',
      reasonPlaceholder: 'Опишіть причину…',
      reasonRequired: 'Введіть причину',
      qtyInvalid: 'Невірне значення',
      submit: 'Зберегти',
      open: 'Редагувати фактичні значення',
    },
    lineColumns: {
      idx: '№',
      material: 'Матеріал',
      part: 'Назва деталі',
      assembly: 'Вузол',
      planned: 'Заплановано',
      actual: 'Фактично',
      issued: 'Видано',
      released: 'Здано',
      availableSnapshot: 'Залишок на момент видачі',
      deficit: 'Ще треба запустити',
      notes: 'Нотатки',
      lengthMm: 'Довжина, мм',
      length: 'Довжина',
      materialLength: 'Довжина матеріалу',
      drawing: 'Креслення',
      qty: 'Кількість',
      unitOfMeasure: 'Одиниця виміру',
      warehouse: 'Склад',
      marker: 'Вид',
      weightKg: 'Вага, кг',
      actualLengthMm: 'Факт. довжина, мм',
      actualWeightKg: 'Факт. вага, кг',
      storekeeper: 'Комірник',
      operation: 'Операція',
      normHours: 'Норма часу, год',
      lineStatus: 'Статус',
    },
    lineEmpty: {
      issue: 'Немає рядків видачі',
      output: 'Немає рядків випуску',
      return: 'Немає рядків повернення',
      task: 'Немає завдань',
      cutPlan: 'План розкрою порожній',
    },
    cutPlanLineLabel: 'Джерело',
    cutPlanLineUnit: 'мм',
    transitions: {
      posted: 'Наряд проведено',
      unposted: 'Наряд повернуто у чернетку',
      deleted: 'Наряд видалено',
    },
    confirmPost: {
      title: 'Провести наряд на роботу?',
      confirm: 'Провести',
      cancel: 'Скасувати',
      allowDeviation: 'Закрити з відхиленням (недостача)',
      deviationReason: 'Причина',
      backdateToggle: 'Заднім числом',
      backdateLabel: 'Дата і час проведення',
      backdateHint: 'Дата проведення для документа. Час фактичного запису фіксується окремо.',
    },
    confirmUnpost: {
      title: 'Розпровести наряд на роботу?',
      description:
        'Наряд на роботу знову стане редагованим. Спершу розпроведіть усі проведені документи за цим нарядом.',
      confirm: 'Розпровести',
    },
    confirmDelete: {
      title: 'Видалити наряд на роботу?',
      description: 'Видалення безповоротне. Доступне тільки для чернетки без дочірніх документів.',
      blockerTitle: 'Видалення заблоковано:',
      preflightLoading: 'Перевірка можливості видалення…',
      confirm: 'Видалити наряд на роботу',
      pending: 'Видалення…',
    },
    shortcuts: {
      post: 'Ctrl/Cmd+Enter',
      cancel: 'Esc щоб закрити підтвердження',
      focusFirst: 'e',
    },
    rail: {
      status: 'Статус',
      transitions: 'Переходи',
      postedHint: 'Наряд проведено. Щоб змінити — розпроведіть його.',
      sections: 'Розділи',
      documents: 'Документи',
    },
    subline: {
      project: 'Проєкт',
      worker: 'Виконавець',
      postedShort: 'Проведено',
    },
    kpi: {
      // cutter
      stocks: 'Хлистів',
      sheets: 'Листів',
      blanks: 'Заготовок',
      placements: 'Розкроєно',
      placementsSub: 'розміщень',
      yield: 'Корисний вихід',
      scrap: 'Відхід',
      remnant: 'Залишок',
      tasks: 'Завдань',
      plannedHours: 'План год.',
      issued: 'Видано',
      output: 'Здано',
    },
    availability: {
      mmSuffix: 'мм',
      maxBuildableSuffix: 'шт можна зварити',
      maxBuildablePrefix: 'до',
      noParts: 'немає деталей',
      noBom: 'Специфікацію не задано',
      nestedBom: '—',
    },
    taskAdd: {
      trigger: 'Додати завдання',
      fields: {
        operation: 'Операція',
        operationPlaceholder: 'Виберіть операцію',
        target: 'Деталь / Вузол',
        targetPlaceholderPart: 'Виберіть деталь',
        targetPlaceholderAssembly: 'Виберіть вузол',
        quantity: 'Кількість',
      },
      save: 'Зберегти',
      cancel: 'Скасувати',
      saving: 'Збереження…',
      created: 'Завдання додано',
      updated: 'Завдання оновлено',
      deleted: 'Завдання видалено',
      editAria: 'Редагувати завдання',
      deleteAria: 'Видалити завдання',
      deleteConfirm: {
        title: 'Видалити завдання?',
        description: 'Дію не можна скасувати.',
        confirm: 'Видалити',
      },
      validation: {
        allRequired: 'Заповніть операцію, ціль та кількість',
      },
    },
    materialAutoFill: {
      trigger: 'Заповнити з матеріалу',
      triggerDisabled: 'Немає деталей з дефіцитом у проекті',
      dialogTitle: 'Заповнити завдання з матеріалу',
      search: 'Пошук матеріалу…',
      filterDeficitOnly: 'Тільки з дефіцитом',
      filterStockOnly: 'Тільки з наявним на складі',
      noMaterials: 'Немає матеріалів за критеріями',
      columns: {
        material: 'Матеріал',
        partsCount: 'Деталей',
        deficit: 'Дефіцит',
        stock: 'На складі',
      },
      previewPlaceholder: 'Виберіть матеріал зліва, щоб побачити деталі',
      previewEmpty: 'Усі деталі цього матеріалу вже у завданні або без дефіциту',
      previewHeading: 'Деталі для додавання',
      previewColumns: {
        select: '',
        name: 'Назва',
        length: 'Довжина',
        qty: 'Кількість',
      },
      selectAll: 'Виділити всі',
      selectNone: 'Зняти виділення',
      cancel: 'Скасувати',
      submit: (n: number) => `Додати ${n} ${pluralizeParts(n)}`,
      submitAndMore: 'Додати й вибрати інший матеріал',
      submitting: 'Додавання…',
      result: {
        addedOnly: (n: number) => `Додано ${n} ${pluralizeParts(n)}`,
        addedAndSkipped: (added: number, skipped: number) =>
          `Додано ${added} ${pluralizeParts(added)}, пропущено ${skipped} (вже у завданні)`,
      },
      requiresTransferBadge: 'Інший проєкт — потрібен Transfer',
      crossProjectHint:
        'Цей матеріал лежить у складі іншого проєкту. Створіть Transfer, щоб перенести його у свій склад, і тоді його можна буде використати.',
      rowDisabledHint:
        'Увесь запас цього матеріалу — у складах інших проєктів. Виконайте Transfer, щоб обрати матеріал.',
      crossProjectStockLabel: (n: number) => `у чужих складах: ${n}`,
    },
    assemblyAutoFill: {
      trigger: 'Заповнити з вузлів',
      dialogTitle: 'Заповнити завдання з вузлів',
      search: 'Пошук вузла…',
      addAllBuildable: (n: number) => `Додати всі, що можна зібрати (${n})`,
      noAssemblies: 'Немає вузлів за критеріями',
      columns: {
        assembly: 'Вузол',
        partsCount: 'Деталей',
        buildable: 'Можна зібрати',
      },
      buildableNested: 'під-вузол',
      buildableUnknown: '—',
      subassemblyBadge: 'Має під-вузол',
      subassemblyHint:
        'Цей вузол містить під-вузол. Наряд зварювальника складає лише з деталей — під-вузол збирається окремим нарядом.',
      cartHeading: 'Вузли до складання',
      cartEmpty: 'Оберіть вузли зліва, щоб додати їх до завдання',
      cartColumns: {
        assembly: 'Вузол',
        qty: 'К-сть',
        remove: '',
      },
      previewHeading: 'Деталі до видачі',
      previewColumns: {
        name: 'Деталь',
        qty: 'Кількість',
      },
      previewEmpty: 'Обрані вузли не містять деталей',
      qtyLabel: 'К-сть',
      maxBuildableHint: (n: number) => `можна зібрати: ${n}`,
      replaceWarning: 'Заповнення замінить наявні завдання й видачу наряду.',
      cancel: 'Скасувати',
      submit: (n: number) => `Заповнити (${n} ${pluralizeAssemblies(n)})`,
      submitting: 'Заповнення…',
      result: (tasks: number, parts: number) =>
        `Заповнено: ${tasks} ${pluralizeAssemblies(tasks)}, ${parts} ${pluralizeParts(parts)} до видачі`,
    },
  },
  woCreate: {
    title: 'Новий наряд',
    hint: 'Створиться як чернетка — деталі заповните на сторінці наряду',
    fields: {
      type: 'Тип наряду',
      workDate: 'Дата робіт',
      team: 'Бригада',
      notes: 'Нотатки',
    },
    typeHints: {
      cutter: 'Порізка довгомірів',
      welder: 'Зварювання вузлів з деталей',
    },
    submit: 'Створити',
    pending: 'Створюємо…',
    success: 'Наряд створено',
    error: 'Не вдалося створити наряд',
    foremenEmpty: 'Активних майстрів немає',
    foremenError: 'Не вдалося завантажити список майстрів',
  },
  cutPlan: {
    legend: {
      placements: 'розкрій',
      remnant: 'залишок',
      scrap: 'відходи',
      total: 'всього',
      sheet: 'лист',
      parts: (n: number) => `деталей ${n}`,
      used: 'використано по довжині',
    },
    joinPiece: 'частина під зварний стик',
    sheetHint: 'Деталі лягають смугами поперек листа; заштриховано — обрізок, сіре — відхід.',
    segments: {
      placement: 'розкрій',
      remnant: 'Залишок',
      scrap: 'Відходи',
    },
    unit: 'мм',
    editor: {
      title: 'Редагування плану',
      buttons: {
        edit: 'Редагувати',
        done: 'Готово',
        revert: 'Скинути',
        close: 'Закрити',
        addSource: 'Додати джерело',
        addJoin: "Об'єднати залишки",
        deleteSlab: 'Видалити',
        addPlacement: 'Додати деталь',
        cancel: 'Скасувати',
      },
      labels: {
        kerf: 'Пропил',
        part: 'Деталь',
        length: 'Довжина',
        material: 'Матеріал',
        source: 'Хлист-джерело',
      },
      sync: {
        clean: 'Синхронізовано з нарядом',
        dirty: 'План змінено',
      },
      empty: {
        bar: 'Ще немає хлистів. Натисніть «Додати джерело».',
        join: 'Зварених деталей немає',
      },
      toasts: {
        success: 'План розкрою оновлено',
        failed: 'Не вдалося зберегти план',
      },
      joinSectionTitle: "Зварені з'єднання",
      hintAddPlacementUnit: 'мм доступно',
      hintKerfMm: 'мм пропил',
      unpostBeforeEditTitle: 'Зняти проведення?',
      unpostBeforeEditBody:
        'Цей наряд проведено. Для редагування плану треба спершу зняти проведення.',
      unpostBeforeEditConfirm: 'Зняти проведення',
      sheetOnlyOptimizer:
        'Розкладку листа будує оптимізатор: змініть завдання й натисніть «Оптимізувати».',
    },
  },
  optimize: {
    title: 'Запустити оптимізацію розкрою?',
    description: 'Розкладе деталі на хлисти й листи зі складу — спершу на обрізки, потім на цілі.',
    running: 'Оптимізація триває…',
    run: 'Запустити',
    fields: {
      kerf: 'Ширина пилки (мм)',
      threshold: 'Поріг залишку (мм)',
      allowJoin: 'Дозволити зварний стик: довгу деталь — з двох частин',
      kerfHint: 'Зазвичай 2-3 мм',
      thresholdHint: 'Коротший шматок піде у відхід, довший — на склад обрізків',
    },
    replaceWarning: (sources: string) =>
      `У наряді вже є план розкрою (${sources}). Оптимізація замінить поточний план — ручні зміни в ньому буде втрачено.`,
    deficits: {
      title: 'Не вистачає матеріалу',
      material: 'Матеріал',
      short: 'Бракує',
      parts: 'Для деталей',
      toReceipt: 'Прихідна накладна',
      toTransfer: 'Переміщення',
      toReport: 'Звіт дефіцитів',
      sheets: (n: number, m2: string) => `≈${n} лист. (${m2} м²)`,
    },
    toasts: {
      success: 'Оптимізацію виконано',
      failed: 'Не вдалося оптимізувати',
    },
  },
  optimizeProduction: {
    trigger: 'Згенерувати видачу',
    running: 'Генерація…',
    toasts: {
      success: 'Видачу та випуск згенеровано',
      failed: 'Не вдалося згенерувати',
    },
  },
  inventory: {
    subtitle: 'Лоти та матеріали проєкту',
    tabs: {
      lots: 'Лоти',
      materials: 'Матеріали',
    },
    columns: {
      id: '№',
      itemKind: 'Тип',
      code: 'Код',
      name: 'Назва',
      origin: 'Походження',
      quantity: 'Кількість',
      createdAt: 'Створено',
      materialName: 'Матеріал',
      stockType: 'Вид номенклатури',
      status: 'Статус',
      lengthMm: 'Довжина, мм',
      weightKg: 'Вага, кг',
      title: 'Назва',
      documentNumber: 'Документ',
      postedAt: 'Дата проводки',
    },
    badges: {
      raw: 'Сировина',
      remnant: 'Залишок',
      part: 'Деталь',
      assembly: 'Складання',
      available: 'Вільний',
      reserved: 'Резерв',
      consumed: 'Списано',
      receipt: 'Прихід',
      workOrder: 'Наряд на роботу',
      adjustment: 'Коригування',
      draft: 'Чернетка',
      posted: 'Проведено',
      cancelled: 'Скасовано',
    },
    empty: {
      lots: 'Лотів не знайдено',
      materials: 'Матеріалів не знайдено',
    },
    pagination: {
      of: 'з',
      prev: 'Попередня',
      next: 'Наступна',
    },
  },
  storekeeping: {
    title: 'Видача матеріалів',
    subtitle: 'Проведені наряди, яким ще треба видати матеріал, і що вже видано сьогодні.',
    allIssues: 'Усі видачі',
    openWorkOrder: 'Відкрити наряд',
    openIssue: 'Відкрити накладну',
    pending: {
      title: 'Очікують видачі',
      count: (n: number) => `${n} ${pluralUk(n, 'наряд', 'наряди', 'нарядів')}`,
      emptyBody: 'Коли майстер проведе наряд на роботу, він з’явиться тут.',
      columns: {
        number: 'Наряд на роботу',
        project: 'Проєкт',
        workDate: 'Дата',
        lines: 'Невидано позицій',
        remaining: 'Залишилось видати',
        action: 'Дія',
      },
      empty: 'Немає нарядів на роботу на видачу',
      issueButton: 'Видати',
    },
    today: {
      title: 'Видано сьогодні',
      columns: {
        number: 'Накладна',
        workOrder: 'Наряд на роботу',
        recipient: 'Отримувач',
        lines: 'Позицій',
        time: 'Час',
      },
      empty: 'Сьогодні видач не було',
      count: (notes: number, lines: number) =>
        `${notes} ${pluralUk(notes, 'накладна', 'накладні', 'накладних')} · ${lines} ${pluralUk(lines, 'позиція', 'позиції', 'позицій')}`,
    },
  },
  errors: {
    unknown: 'Не вдалося виконати дію. Спробуйте ще раз.',
    network: "Не вдалося з'єднатися з сервером",
    forbidden: 'Доступ заборонено',
    notFound: 'Сторінку не знайдено',
    serverError: 'Помилка сервера',
    validationFailed: 'Перевірте правильність даних',
    conflict: 'Документ змінено — оновіть сторінку',
    byStatus: {
      400: 'Перевірте правильність даних',
      401: 'Сесія завершилась — увійдіть знову',
      403: 'Доступ заборонено',
      404: 'Запис не знайдено',
      409: 'Документ змінено — оновіть сторінку та спробуйте ще раз',
      422: 'Перевірте правильність даних',
      429: 'Забагато запитів — зачекайте хвильку',
      500: 'Помилка на сервері. Спробуйте пізніше або зверніться до адміністратора.',
      502: "Сервер недоступний. Перевірте з'єднання.",
      503: 'Сервіс тимчасово недоступний. Спробуйте за кілька хвилин.',
      504: 'Сервер не відповідає. Спробуйте ще раз.',
    },
  },
  references: {
    hub: {
      title: 'Довідники',
      subtitle: 'Люди, склади, посади, операції й одиниці, на які спираються документи і наряди',
      sections: {
        users: 'Люди',
        warehouses: 'Склади',
        positions: 'Посади',
        operations: 'Операції',
        units: 'Одиниці виміру',
      },
    },
    common: {
      newItem: 'Створити',
      empty: 'Записів немає',
      search: 'Пошук',
      activityFilter: 'Показати',
      activity: { active: 'Активні', archived: 'Архів', all: 'Усі' },
      activeLabel: 'активний',
      status: 'Статус',
      createFirst: 'Додайте перший запис кнопкою «Створити».',
      nothingFound: 'Нічого не знайдено',
      changeFilters: 'Змініть пошук або покажіть архів.',
      resetFilters: 'Скинути фільтри',
      shown: 'Показано {n} з {total}',
      save: 'Зберегти',
      saving: 'Збереження…',
      cancel: 'Скасувати',
      delete: 'Видалити',
      deleting: 'Видалення…',
      created: 'Створено',
      saved: 'Збережено',
      deleted: 'Видалено',
      actions: 'Дії',
      edit: 'Редагувати',
      saveError: 'Не вдалося зберегти',
      deleteError: 'Не вдалося видалити',
    },
    units: {
      title: 'Одиниці виміру',
      subtitle: 'Одиниці, в яких рахують матеріали, деталі й вузли.',
      searchPlaceholder: 'Код, назва або скорочення',
      empty: 'Одиниць виміру ще немає',
      columns: { code: 'Код', name: 'Найменування', shortName: 'Скорочення', status: 'Статус' },
      forms: { code: 'Код', name: 'Найменування', shortName: 'Скорочення (м, кг, шт)' },
      deleteConfirm:
        'Видалити одиницю виміру? Дія незворотна. Якщо одиниця використовується у деталях / вузлах / матеріалах — видалення буде заблоковано.',
    },
    operations: {
      title: 'Операції',
      subtitle:
        'Роботи, які цех виконує за нарядами. Тип наряду визначає, де операцію можна обрати.',
      searchPlaceholder: 'Код або назва',
      empty: 'Операцій ще немає',
      columns: {
        code: 'Код',
        name: 'Назва',
        woTypes: 'Типи нарядів на роботу',
        status: 'Статус',
      },
      forms: { code: 'Код', name: 'Назва', woTypes: 'Типи нарядів на роботу' },
      woTypeLabels: { cutter: 'Різання', welder: 'Зварювання' },
      deleteConfirm:
        'Видалити операцію? Заблоковано, якщо хоч один наряд на роботу її вже використовує.',
    },
    positions: {
      title: 'Посади',
      subtitle: 'Посади, які призначають людям. Посаду, яку хтось обіймає, видалити не можна.',
      searchPlaceholder: 'Код або назва',
      empty: 'Посад ще немає',
      columns: { code: 'Код', name: 'Назва', status: 'Статус' },
      forms: { code: 'Код', name: 'Назва' },
      deleteConfirm:
        'Видалити посаду? Заблоковано, якщо до неї прив’язано хоча б одного користувача.',
    },
    users: {
      title: 'Користувачі',
      subtitle:
        'Працівники цеху й офісу. Табельний номер — головний ідентифікатор; вхід у систему потрібен не всім.',
      searchPlaceholder: 'Табельний, ПІБ або посада',
      empty: 'Людей ще немає',
      columns: {
        tab_no: 'Табельний',
        full_name: 'ПІБ',
        position: 'Посада',
        status: 'Статус',
      },
      forms: {
        tab_no: 'Табельний №',
        firstName: 'Ім’я',
        lastName: 'Прізвище',
        patronymic: 'По-батькові',
        position: 'Посада',
        positionPlaceholder: 'Без посади',
      },
      noLoginHint:
        'Створюється без логіну. Електронна пошта та доступ до системи додаються через Django admin.',
    },
    warehouses: {
      title: 'Склади',
      subtitle: 'Де лежать метал, обрізки й готова продукція. У кожного складу є відповідальний.',
      empty: 'Складів ще немає',
      new: 'Новий склад',
      edit: 'Редагувати склад',
      backToList: 'До списку',
      searchPlaceholder: 'Пошук за кодом, назвою…',
      columns: {
        code: 'Код',
        name: 'Назва',
        project: 'Проєкт',
        responsible: 'Відповідальний',
        status: 'Статус',
      },
      forms: {
        code: 'Код',
        name: 'Назва',
        project: 'Проєкт',
        projectPlaceholder: 'Глобальний (без проєкту)',
        responsible: 'Відповідальний',
        responsiblePlaceholder: 'Оберіть користувача',
        notes: 'Примітка',
      },
      validation: {
        codeRequired: 'Код обовʼязковий',
        nameRequired: 'Назва обовʼязкова',
        responsibleRequired: 'Оберіть відповідального',
      },
      deleteConfirm:
        'Видалити склад {name}? Дію не можна відмінити. Видалення заблоковано, якщо на складі є залишки.',
    },
  },
  goodsReceipts: {
    title: 'Прихідний ордер на товар',
    subtitle: 'Накладні прийому готових деталей / збірок / залишків з виробництва',
    empty: 'Документів немає',
    create: 'Створити',
    backToList: 'До списку',
    searchPlaceholder: 'Пошук за номером / проєктом…',
    statusFilters: {
      all: 'Усі',
      draft: 'Чернетка',
      posted: 'Проведений',
    },
    columns: {
      number: 'Номер',
      date: 'Дата',
      project: 'Проєкт',
      lines: 'Рядків',
      status: 'Статус',
    },
    lineColumns: {
      number: '№',
      partName: 'Позиція',
      partCode: 'Код (маркер)',
      length: 'Довжина, мм',
      qty: 'Кількість',
      unit: 'Од.виміру',
      warehouse: 'Склад',
    },
    remnantMark: 'обрізок',
    returnMark: 'повернення',
    returnLabel: 'Повернення виданого',
    returnHint:
      'Невикористані деталі, видані цим нарядом, повертаються на склад тими самими штуками — нові не створюються.',
    actions: {
      post: 'Провести',
      unpost: 'Розпровести',
      delete: 'Видалити',
    },
    confirm: {
      postTitle: 'Провести прийом?',
      postDescription:
        'Залишки на складі будуть збільшені згідно з рядками документа. Дія створює транзакції в журналі руху.',
      unpostTitle: 'Розпровести прийом?',
      unpostDescription:
        'Залишки будуть зменшені дзеркальними транзакціями. Заблоковано, якщо позиції вже спожиті.',
      deleteTitle: 'Видалити чернетку?',
      deleteDescription: 'Дія незворотна. Видаляються лише чернетки.',
    },
    toasts: {
      posted: 'Прийом проведено',
      unposted: 'Прийом розпроведено',
      deleted: 'Чернетку видалено',
      created: 'Прийом створено',
    },
    lines: {
      target: 'Об’єкт',
      qty: 'Кількість',
      lengthMm: 'Довжина, мм',
      remnant: 'Залишок',
      notes: 'Коментар',
      empty: 'Рядків немає',
      remnantYes: 'так',
      material: 'Матеріал',
      part: 'Деталь',
      assembly: 'Вузол',
    },
    form: {
      sectionHeader: 'Заголовок',
      sectionLines: 'Рядки прийому',
      project: 'Проєкт',
      storekeeper: 'Комірник',
      receiptDate: 'Дата прийому',
      notes: 'Коментар',
      addLine: 'Додати рядок',
      removeLine: 'Видалити',
      target: 'Об’єкт',
      targetMaterial: 'Матеріал',
      targetPart: 'Деталь',
      targetAssembly: 'Вузол',
      qty: 'Кількість',
      lengthMm: 'Довжина, мм',
      weightKg: 'Вага, кг',
      isRemnant: 'Залишок (remnant)',
      lineNotes: 'Коментар рядка',
      itemId: 'ID',
      saveDraft: 'Зберегти чернетку',
      saveAndPost: 'Зберегти і провести',
      atLeastOneLine: 'Потрібно щонайменше один рядок',
      pickTarget: 'Вкажіть матеріал / деталь / вузол',
    },
  },
  materialIssues: {
    title: 'Розхідний ордер на товар',
    subtitle: 'Видачі матеріалів зі складу в цех за всіма нарядами',
    empty: 'Видач не знайдено',
    create: 'Створити',
    columns: {
      number: '№',
      issueDate: 'Дата',
      project: 'Проєкт',
      wo: 'Наряд на роботу',
      sourceWarehouse: 'Склад',
      storekeeper: 'Видав',
      recipient: 'Одержувач',
      lines: 'Рядків',
      status: 'Статус',
    },
    /** Unified columns for both flat-list and per-doc line table. */
    lineColumns: {
      number: '№',
      name: 'Назва матеріалу',
      length: 'Довжина матеріалу',
      qty: 'Кількість',
      unit: 'Од.виміру',
      warehouse: 'Склад',
      marker: 'Маркер',
    },
    fields: {
      sourceWarehouse: 'Склад джерело',
      sourceWarehouseHint: 'Звідки видали',
      sourceWarehousePlaceholder: 'Оберіть склад…',
      sourceWarehouseRequired: 'Обовʼязковий перед проведенням накладної',
    },
  },
  productionReports: {
    title: 'Звіт виробництва за зміну',
    zeroConfirm: {
      title: 'Провести звіт без готових?',
      body: 'У всіх рядках звіту 0 готових. Звіт зафіксує зміну, за яку нічого не виготовлено.',
      confirm: 'Провести все одно',
    },
    subtitle: 'Звіти за зміну за всіма нарядами',
    empty: 'Звітів не знайдено',
    create: 'Створити',
    columns: {
      number: '№',
      shiftDate: 'Дата',
      project: 'Проєкт',
      wo: 'Наряд на роботу',
      worker: 'Виконавець',
      lines: 'Рядків',
      status: 'Статус',
    },
    lineColumns: {
      number: '№',
      partName: 'Назва деталі',
      partCode: 'Номер деталі (маркер)',
      assemblyName: 'Назва вузла',
      assemblyCode: 'Номер вузла (маркер)',
      qty: 'Кількість',
      left: 'Залишок',
      unit: 'Од.виміру',
      warehouse: 'Склад',
    },
  },
  stockReceipts: {
    title: 'Прихідна накладна',
    subtitle: 'Зовнішній прихід матеріалів / деталей / вузлів від постачальників',
    empty: 'Накладних не знайдено',
    create: 'Створити',
    columns: {
      number: '№',
      title: 'Назва',
      documentNumber: 'Документ',
      createdAt: 'Дата',
      project: 'Проєкт',
      destinationWarehouse: 'Склад',
      lines: 'Рядків',
      totalWeightKg: 'Вага, кг',
      status: 'Статус',
    },
    fields: {
      destinationWarehouse: 'Склад призначення',
      destinationWarehouseHint: 'Куди прийшов прихід',
      destinationWarehousePlaceholder: 'Оберіть склад…',
      destinationWarehouseRequired: 'Обовʼязковий перед проведенням накладної',
    },
  },
  transfers: {
    title: 'Переміщення',
    subtitle: 'Переміщення матеріалів та обрізків між складами',
    empty: 'Переміщень не знайдено',
    create: 'Створити переміщення',
    columns: {
      number: '№',
      transferDate: 'Дата',
      sourceWarehouse: 'Звідки',
      destinationWarehouse: 'Куди',
      lines: 'Рядків',
      status: 'Статус',
    },
    lineColumns: {
      number: '№',
      kind: 'Тип',
      target: 'Позиція',
      ownership: 'Власність',
      length: 'Довжина',
      qty: 'Кількість',
      notes: 'Нотатки',
    },
    fields: {
      sourceWarehouse: 'Склад-джерело',
      sourceWarehouseHint: 'Звідки забираємо',
      sourceWarehousePlaceholder: 'Оберіть склад…',
      destinationWarehouse: 'Склад призначення',
      destinationWarehouseHint: 'Куди переміщуємо',
      destinationWarehousePlaceholder: 'Оберіть склад…',
      targetProject: 'Проєкт після переміщення',
      targetProjectHint:
        'Зазвичай штука лишає свій проєкт. Оберіть проєкт, щоб перепризначити, або «Без проєкту» — нічийний',
      keepProject: 'Лишити проєкт штуки',
      targetProjectPlaceholder: 'Без проєкту',
      transferDate: 'Дата переміщення',
      notes: 'Нотатки',
      kindLot: 'Лот матеріалу',
      kindRemnant: 'Обрізок',
      kindStockItem: 'Деталь / вузол',
      noProjectLabel: 'Без проєкту',
      ownershipMove: (from: string, to: string) => `${from} → ${to}`,
    },
    actions: {
      addLine: 'Додати позицію',
      addLineCta: 'Додати першу позицію',
      editLine: 'Редагувати позицію',
      deleteLine: 'Видалити позицію',
      post: 'Провести',
      unpost: 'Розпровести',
      delete: 'Видалити',
      save: 'Зберегти',
      cancel: 'Скасувати',
    },
    dialog: {
      addLineTitle: 'Додати позицію переміщення',
      editLineTitle: 'Редагувати позицію',
      addLineDescription:
        'Оберіть тип джерела (лот, обрізок або деталь/вузол) і конкретну позицію зі складу-джерела.',
      pickSource: 'Спочатку оберіть склад-джерело — без нього немає переліку лотів.',
    },
    posted: 'Документ проведено — змінити його можна, лише розпровівши.',
    emptyLines: 'Позицій ще немає',
    emptyLinesBody: 'Додайте лот матеріалу або обрізок зі складу-джерела.',
    cannotPostHint: 'Заповніть склади, дату і хоча б один рядок.',
  },
  stock: {
    title: 'Склад',
    subtitle: 'Запаси на всіх складах системи',
    empty: 'Нічого не знайдено',
    emptyAll: 'Жодних запасів. Створіть прихідну накладну, щоб додати позиції.',
    emptyFiltered: 'За цими фільтрами нічого не знайдено.',
    emptyWarehouse: (code: string) => `Склад ${code} порожній.`,
    resetFilters: 'Скинути фільтри',
    pagination: {
      page: (n: number, total: number) => `Сторінка ${n} з ${total}`,
      positions: (n: number) =>
        `${n} ${n === 1 ? 'позиція' : n >= 2 && n <= 4 ? 'позиції' : 'позицій'}`,
      prev: 'Назад',
      next: 'Далі',
      pageSize: 'На сторінці:',
    },
    columns: {
      code: 'Код',
      name: 'Назва',
      size: 'Розмір',
      warehouse: 'Склад',
      quantity: 'Кількість',
    },
    types: {
      material: 'Матеріал',
      remnant: 'Обрізок',
      part: 'Деталь',
      assembly: 'Вузол',
    },
    sortAriaLabel: (col: string, dir: string) =>
      `Сортувати за ${col} (${dir === 'asc' ? 'за зростанням' : 'за спаданням'})`,
    filters: {
      searchPlaceholder: 'Пошук за кодом або назвою',
      chipAll: 'Усі',
    },
    kpi: {
      totalQuantity: 'Усього одиниць',
      totalGroups: 'Різних позицій',
      remnants: 'Обрізків',
      finished: 'Готових деталей і вузлів',
    },
    expand: {
      header: 'Походження',
      loading: 'Завантаження…',
      empty: 'Немає історичних джерел',
      unit: 'шт',
      summary: (n: number, qty: number) =>
        `${n} ${n === 1 ? 'джерело' : n < 5 ? 'джерела' : 'джерел'} · разом ${qty} шт`,
      originLabels: {
        work_order: 'Наряд',
        receipt: 'Прихідний ордер',
        adjustment: 'Коригування',
      },
      goToSource: 'Перейти до джерела',
    },
  },
  reports: {
    title: 'Звіти',
    filters: {
      periodFrom: 'Період з',
      periodTo: 'по',
      warehouse: 'Склад',
      project: 'Проєкт',
      group: 'Група номенклатури',
      grouping: 'Групування',
      reset: 'Скинути',
    },
    grouping: {
      by_warehouse: 'За складом',
      by_material: 'За матеріалом',
      by_project: 'За проєктом',
    },
    columns: {
      name: 'Назва',
      unit: 'Од.',
      opening: 'Початок',
      receipt: 'Прихід',
      issue: 'Витрата',
      balance: 'Залишок',
    },
    units: {
      pieces: 'Штуки',
      measure: 'Метраж · вага',
    },
    views: {
      movements: 'Рух',
      balances: 'Залишки',
      consumption: 'Витрати',
    },
    picker: {
      prompt: 'Оберіть проєкт',
      changeProject: 'Змінити проєкт',
      empty: 'Проєктів не знайдено.',
    },
    empty: 'Немає даних за обраними фільтрами.',
    generatedAt: 'Згенеровано',
    stockMovements: {
      title: 'Склад: рух і залишки',
      description:
        'Прихід / Витрата / Закл. залишок по матеріалах, обрізках, деталях і вузлах. Фільтри: період, проєкт, склад, тип, група.',
    },
    stockBalances: {
      title: 'Складські залишки',
      description:
        'Кінцевий залишок по матеріалах, обрізках, деталях і вузлах станом на кінець обраного періоду.',
    },
    materialConsumption: {
      title: 'Витрати матеріалів',
      description: 'Витрата сировини за обраний період. Фільтри: проєкт, склад, група.',
    },
    deficit: {
      title: 'Дефіцит / Готовність',
      description: 'Потрібно · Зроблено · Не вистачає по кожному вузлу і деталі проєкту.',
    },
  },
  documents: {
    /** «Далі» card on a posted document — the next link of наряд → видача → звіт → прийом. */
    nextStep: {
      issue: {
        title: 'Видати матеріал у цех',
        hint: 'Розхідний ордер заповниться тим, що наряд ще не отримав зі складу.',
        action: 'Видати матеріал',
      },
      issueParts: {
        title: 'Видати деталі у цех',
        hint: 'Розхідний ордер заповниться деталями, яких наряд ще не отримав зі складу.',
        action: 'Видати деталі',
      },
      report: {
        title: 'Звіт виробництва за зміну',
        hint: 'Звіт заповниться завданнями наряду, готових — 0: внесіть у таблиці, скільки зроблено за зміну.',
        action: 'Створити звіт',
      },
      receipt: {
        title: 'Прихідний ордер на склад',
        hint: 'Ордер заповниться тим, що виготовлено за цим звітом, і тим, що варто повернути: невикористані деталі, обрізки, непорізаний матеріал.',
        action: 'Створити прихідний ордер',
        done: 'Прихідний ордер уже створено',
        open: 'Відкрити прихідний ордер',
      },
      backToWo: {
        title: 'Наряд',
        hint: 'Перевірте, що ще лишилося зробити за нарядом.',
        action: 'Повернутися до наряду',
      },
    },
    common: {
      deleteLineTitle: 'Видалити позицію?',
      post: 'Провести',
      unpost: 'Розпровести',
      saving: 'Зберігається…',
      saved: 'Збережено',
      postBlockersTitle: 'Щоб провести:',
      blockers: {
        project: 'Оберіть проєкт',
        warehouse: 'Оберіть склад',
        sourceWarehouse: 'Оберіть склад, звідки переміщуєте',
        destinationWarehouse: 'Оберіть склад, куди переміщуєте',
        recipient: 'Оберіть одержувача',
        lines: 'Додайте хоча б одну позицію',
        woOtherProject: 'Наряд належить іншому проєкту — оберіть інший',
      },
      history: 'Історія',
      postedBanner: 'Документ проведено — змінити його можна, лише розпровівши.',
      postedHint: 'Проведено — лише перегляд',
      woOtherProject: 'Наряд на роботу з іншого проєкту',
      statusLabel: 'Статус',
      projectLabel: 'Проєкт',
      projectAll: 'Усі проєкти',
      noneYet: 'Документів ще немає',
      search: 'Пошук',
      searchPlaceholder: 'Номер або наряд',
      emptyTitle: 'Нічого не знайдено за фільтрами',
      emptyBody: 'Змініть або скиньте фільтри.',
      resetFilters: 'Скинути фільтри',
      iter2FieldsLabel: 'Поля Iter 2 (приховано до wiring):',
      iter2Default:
        'У поточній ітерації документ обробляє лише складські рухи. Цінові поля існують у моделі, але приховані з UI до моменту інтеграції з блоком обліку (Iter 2).',
      status: {
        all: 'Усі',
        draft: 'Чернетка',
        posted: 'Проведено',
      },
      confirmUnpost: {
        title: (id: string) => `Розпровести ${id}?`,
        body: 'Документ повернеться в чернетку, а зміни на складі, які він зробив, буде скасовано.',
      },
      confirmDelete: {
        title: (id: string) => `Видалити чернетку ${id}?`,
        body: 'Чернетку буде видалено назавжди.',
      },
    },
  },
} as const

type Messages = typeof messages
